/**
 * AnalysisChartConfigPanel Component
 *
 * A single-column chart configuration panel for the AnalysisBuilder.
 * Uses fields from the Query tab (metrics/breakdowns) as available fields.
 * Renders axis drop zones and display options based on chart type.
 */

import { useMemo, useEffect, useState, useCallback, DragEvent } from 'react'
import { getIcon, getMeasureTypeIcon } from '../../icons/index.js'
import SectionHeading from './SectionHeading.js'
import AnalysisAxisDropZone from './AnalysisAxisDropZone.js'
import ChartTypeSelector from '../ChartTypeSelector.js'
import { useChartConfig } from '../../charts/lazyChartConfigRegistry.js'
import type { ChartType, ChartAxisConfig } from '../../types.js'
import type { MetricItem, BreakdownItem } from './types.js'
import type { ChartAvailabilityMap } from '../../shared/chartDefaults.js'
import type { MetaResponse } from '../../shared/types.js'
import { useTranslation } from '../../hooks/useTranslation.js'
import { applyAxisDrop, applyAxisRemove, applyAxisReorder } from './utils/axisConfigUtils.js'

const MeasureIcon = getIcon('measure')
const DimensionIcon = getIcon('dimension')
const TimeDimensionIcon = getIcon('timeDimension')

interface AnalysisChartConfigPanelProps {
  chartType: ChartType
  chartConfig: ChartAxisConfig
  metrics: MetricItem[]
  breakdowns: BreakdownItem[]
  /** Schema metadata for resolving field titles */
  schema?: MetaResponse | null
  /** Map of chart type availability for disabling unavailable chart types */
  chartAvailability?: ChartAvailabilityMap
  onChartTypeChange: (type: ChartType) => void
  onChartConfigChange: (config: ChartAxisConfig) => void
}

export default function AnalysisChartConfigPanel({
  chartType,
  chartConfig,
  metrics,
  breakdowns,
  schema,
  chartAvailability,
  onChartTypeChange,
  onChartConfigChange
}: AnalysisChartConfigPanelProps) {
  const { t } = useTranslation()
  // Track currently dragging item for immediate state updates
  const [draggedItem, setDraggedItem] = useState<{
    field: string
    fromAxis: string
    fromIndex?: number
  } | null>(null)

  // Derive available fields from metrics and breakdowns
  const availableFields = useMemo(
    () => ({
      measures: metrics.map((m) => m.field),
      dimensions: breakdowns.filter((b) => !b.isTimeDimension).map((b) => b.field),
      timeDimensions: breakdowns.filter((b) => b.isTimeDimension).map((b) => b.field)
    }),
    [metrics, breakdowns]
  )

  // Get configuration for current chart type
  const { config: chartTypeConfig, loaded: chartConfigLoaded } = useChartConfig(chartType)

  // Check if this chart type skips queries
  const shouldSkipQuery = chartTypeConfig.skipQuery === true

  // Get fields for each drop zone
  const getFieldsForDropZone = useCallback((key: string): string[] => {
    const value = chartConfig[key as keyof ChartAxisConfig]
    const result = Array.isArray(value)
      ? value
      : typeof value === 'string'
        ? [value]
        : []
    return result
  }, [chartConfig])

  // Clean up chart config when available fields change
  useEffect(() => {
    if (!chartConfigLoaded) return
    const allAvailableFields = [
      ...availableFields.dimensions,
      ...availableFields.timeDimensions,
      ...availableFields.measures
    ]

    let hasChanges = false
    const newConfig = { ...chartConfig }

    // Check each axis and remove fields that are no longer available
    chartTypeConfig.dropZones.forEach((dropZone) => {
      const currentFields = getFieldsForDropZone(dropZone.key)
      const validFields = currentFields.filter((field) => allAvailableFields.includes(field))

      if (validFields.length !== currentFields.length) {
        hasChanges = true
        if (validFields.length === 0) {
          // Remove the axis property entirely if no valid fields remain
          delete newConfig[dropZone.key as keyof ChartAxisConfig]
        } else if (dropZone.maxItems === 1) {
          // Single field axis - always store as string
          newConfig[dropZone.key as keyof ChartAxisConfig] = validFields[0] as any
        } else {
          // Multi-field axis - always store as array
          newConfig[dropZone.key as keyof ChartAxisConfig] = validFields as any
        }
      }
    })

    if (hasChanges) {
      onChartConfigChange(newConfig)
    }
  }, [availableFields, chartConfig, chartTypeConfig.dropZones, onChartConfigChange, getFieldsForDropZone, chartConfigLoaded])

  // Helper to determine field type and styling
  const getFieldType = (field: string): 'dimension' | 'timeDimension' | 'measure' => {
    if (availableFields.measures.includes(field)) return 'measure'
    if (availableFields.timeDimensions.includes(field)) return 'timeDimension'
    return 'dimension'
  }

  // Helper to find field metadata from schema
  const findFieldMeta = (fieldName: string) => {
    if (!schema?.cubes) return null

    const [cubeName] = fieldName.split('.')
    const cube = schema.cubes.find((c) => c.name === cubeName)
    if (!cube) return null

    // Check measures first, then dimensions
    const measure = cube.measures?.find((m) => m.name === fieldName)
    if (measure) return { ...measure, fieldType: 'measure' as const }

    const dimension = cube.dimensions?.find((d) => d.name === fieldName)
    if (dimension) return { ...dimension, fieldType: dimension.type === 'time' ? 'timeDimension' as const : 'dimension' as const }

    return null
  }

  // Get field metadata for display in AnalysisAxisDropZone
  const getFieldMeta = (field: string) => {
    const fieldType = getFieldType(field)
    const parts = field.split('.')
    const cubeName = parts[0] || field
    const fieldName = parts[1] || field

    // Look up field metadata from schema
    const schemaMeta = findFieldMeta(field)

    // Try to find the field in breakdowns for isTimeDimension flag
    const breakdownItem = breakdowns.find((b) => b.field === field)

    if (schemaMeta) {
      return {
        title: schemaMeta.title || fieldName,
        shortTitle: schemaMeta.shortTitle || schemaMeta.title || fieldName,
        cubeName,
        type: schemaMeta.fieldType,
        measureType: schemaMeta.fieldType === 'measure' ? schemaMeta.type : undefined
      }
    }

    // Fallback when schema lookup fails
    if (breakdownItem) {
      return {
        title: fieldName,
        shortTitle: fieldName,
        cubeName,
        type: breakdownItem.isTimeDimension ? ('timeDimension' as const) : ('dimension' as const)
      }
    }

    return {
      title: fieldName,
      shortTitle: fieldName,
      cubeName,
      type: fieldType
    }
  }

  // Drag and drop handlers
  const handleDragStart = (
    e: DragEvent<HTMLDivElement>,
    field: string,
    fromAxis: string,
    fromIndex?: number
  ) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ field, fromAxis, fromIndex }))
    setDraggedItem({ field, fromAxis, fromIndex })
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const handleDragEnd = () => {
    setDraggedItem(null)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>, toAxis: string) => {
    e.preventDefault()
    const data = JSON.parse(e.dataTransfer.getData('text/plain'))
    const { field, fromAxis } = data

    const dropZoneConfig = chartTypeConfig.dropZones.find((dz) => dz.key === toAxis)
    const newConfig = applyAxisDrop(chartConfig, field, fromAxis, toAxis, dropZoneConfig)

    setDraggedItem(null)
    onChartConfigChange(newConfig)
  }

  const handleRemoveFromAxis = (field: string, fromAxis: string) => {
    onChartConfigChange(applyAxisRemove(chartConfig, field, fromAxis))
  }

  const handleReorder = (fromIndex: number, toIndex: number, axisKey: string) => {
    const newConfig = applyAxisReorder(chartConfig, fromIndex, toIndex, axisKey)
    if (newConfig) {
      setDraggedItem(null)
      onChartConfigChange(newConfig)
    }
  }

  // Handler for Y-axis assignment changes (dual Y-axis support)
  const handleYAxisAssignmentChange = useCallback(
    (field: string, axis: 'left' | 'right') => {
      onChartConfigChange({
        ...chartConfig,
        yAxisAssignment: {
          ...chartConfig.yAxisAssignment,
          [field]: axis
        }
      })
    },
    [chartConfig, onChartConfigChange]
  )

  if (!chartConfigLoaded) {
    return (
      <div className="dc:space-y-6">
        <div>
          <SectionHeading className="dc:mb-2">{t('chart.config.chartType')}</SectionHeading>
          <ChartTypeSelector
            selectedType={chartType}
            onTypeChange={onChartTypeChange}
            availability={chartAvailability}
            excludeTypes={['funnel', 'sankey', 'sunburst', 'retentionHeatmap', 'retentionCombined']}
            compact
          />
        </div>
        <div className="dc:text-center text-dc-text-muted dc:text-sm dc:py-4">
          {t('chart.config.loading')}
        </div>
      </div>
    )
  }

  // Get unassigned fields (fields selected in Query tab but not yet assigned to chart axes)
  const getUnassignedFields = () => {
    const assignedFields = new Set<string>()
    chartTypeConfig.dropZones.forEach((dz) => {
      getFieldsForDropZone(dz.key).forEach((field) => assignedFields.add(field))
    })

    // Exclude the currently dragged field only if it's being dragged FROM a configured axis
    if (draggedItem && draggedItem.fromAxis !== 'available') {
      assignedFields.add(draggedItem.field)
    }

    return {
      dimensions: availableFields.dimensions.filter((f) => !assignedFields.has(f)),
      timeDimensions: availableFields.timeDimensions.filter((f) => !assignedFields.has(f)),
      measures: availableFields.measures.filter((f) => !assignedFields.has(f))
    }
  }

  const unassignedFields = getUnassignedFields()
  const hasUnassignedFields =
    unassignedFields.dimensions.length > 0 ||
    unassignedFields.timeDimensions.length > 0 ||
    unassignedFields.measures.length > 0

  return (
    <div className="dc:space-y-6">
      {/* Chart Type Selector */}
      <div>
        <SectionHeading className="dc:mb-2">{t('chart.config.chartType')}</SectionHeading>
        <ChartTypeSelector
          selectedType={chartType}
          onTypeChange={onChartTypeChange}
          availability={chartAvailability}
          excludeTypes={['funnel', 'sankey', 'sunburst', 'retentionHeatmap', 'retentionCombined']}
          compact
        />
      </div>

      {/* Chart Axis Configuration - Dynamic Drop Zones */}
      {!shouldSkipQuery && chartTypeConfig.dropZones.length > 0 && (
        <div>
          <SectionHeading className="dc:mb-2">
            {t('chart.config.axisConfig')}
          </SectionHeading>
          <div className="dc:space-y-1">
            {chartTypeConfig.dropZones.map((dropZone) => (
              <AnalysisAxisDropZone
                key={dropZone.key}
                config={dropZone}
                fields={getFieldsForDropZone(dropZone.key)}
                onDrop={handleDrop}
                onRemove={handleRemoveFromAxis}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onReorder={handleReorder}
                draggedItem={draggedItem}
                getFieldMeta={getFieldMeta}
                yAxisAssignment={chartConfig.yAxisAssignment}
                onYAxisAssignmentChange={
                  dropZone.enableDualAxis ? handleYAxisAssignmentChange : undefined
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* Unassigned Fields - Show fields from Query tab that haven't been assigned yet */}
      {!shouldSkipQuery && hasUnassignedFields && (
        <div>
          <div className="dc:mb-2">
            <SectionHeading>{t('chart.config.unassigned')}</SectionHeading>
            <div className="dc:text-xs text-dc-text-muted dc:mt-0.5">
              {t('chart.config.unassignedHint')}
            </div>
          </div>
          <div className="dc:border-2 dc:border-dashed border-dc-border dc:rounded-lg dc:p-2 bg-dc-surface-secondary">
            <div className="dc:space-y-2">
              {/* Measures */}
              {unassignedFields.measures.map((field) => {
                const meta = getFieldMeta(field)
                const isBeingDragged =
                  draggedItem && draggedItem.field === field && draggedItem.fromAxis === 'available'
                const IconComponent = getMeasureTypeIcon(meta.measureType || 'count') || MeasureIcon
                return (
                  <div
                    key={field}
                    draggable
                    onDragStart={(e) => handleDragStart(e, field, 'available')}
                    onDragEnd={handleDragEnd}
                    className={`dc:flex dc:items-center dc:gap-2 dc:p-2 bg-dc-surface dc:rounded-lg hover:bg-dc-surface-tertiary dc:transition-colors dc:cursor-move ${isBeingDragged ? 'dc:opacity-50 dc:cursor-grabbing' : ''}`}
                    title={field}
                  >
                    <span className="dc:w-6 dc:h-6 dc:flex dc:items-center dc:justify-center dc:rounded bg-dc-measure text-dc-measure-text dc:flex-shrink-0">
                      <IconComponent className="dc:w-4 dc:h-4" />
                    </span>
                    <div className="dc:flex-1 dc:min-w-0">
                      <div className="dc:text-sm text-dc-text dc:truncate">{meta.shortTitle}</div>
                      <div className="dc:text-xs text-dc-text-muted dc:truncate">{meta.cubeName}</div>
                    </div>
                  </div>
                )
              })}

              {/* Dimensions */}
              {unassignedFields.dimensions.map((field) => {
                const meta = getFieldMeta(field)
                const isBeingDragged =
                  draggedItem && draggedItem.field === field && draggedItem.fromAxis === 'available'
                return (
                  <div
                    key={field}
                    draggable
                    onDragStart={(e) => handleDragStart(e, field, 'available')}
                    onDragEnd={handleDragEnd}
                    className={`dc:flex dc:items-center dc:gap-2 dc:p-2 bg-dc-surface dc:rounded-lg hover:bg-dc-surface-tertiary dc:transition-colors dc:cursor-move ${isBeingDragged ? 'dc:opacity-50 dc:cursor-grabbing' : ''}`}
                    title={field}
                  >
                    <span className="dc:w-6 dc:h-6 dc:flex dc:items-center dc:justify-center dc:rounded bg-dc-dimension text-dc-dimension-text dc:flex-shrink-0">
                      <DimensionIcon className="dc:w-4 dc:h-4" />
                    </span>
                    <div className="dc:flex-1 dc:min-w-0">
                      <div className="dc:text-sm text-dc-text dc:truncate">{meta.shortTitle}</div>
                      <div className="dc:text-xs text-dc-text-muted dc:truncate">{meta.cubeName}</div>
                    </div>
                  </div>
                )
              })}

              {/* Time Dimensions */}
              {unassignedFields.timeDimensions.map((field) => {
                const meta = getFieldMeta(field)
                const isBeingDragged =
                  draggedItem && draggedItem.field === field && draggedItem.fromAxis === 'available'
                return (
                  <div
                    key={field}
                    draggable
                    onDragStart={(e) => handleDragStart(e, field, 'available')}
                    onDragEnd={handleDragEnd}
                    className={`dc:flex dc:items-center dc:gap-2 dc:p-2 bg-dc-surface dc:rounded-lg hover:bg-dc-surface-tertiary dc:transition-colors dc:cursor-move ${isBeingDragged ? 'dc:opacity-50 dc:cursor-grabbing' : ''}`}
                    title={field}
                  >
                    <span className="dc:w-6 dc:h-6 dc:flex dc:items-center dc:justify-center dc:rounded bg-dc-time-dimension text-dc-time-dimension-text dc:flex-shrink-0">
                      <TimeDimensionIcon className="dc:w-4 dc:h-4" />
                    </span>
                    <div className="dc:flex-1 dc:min-w-0">
                      <div className="dc:text-sm text-dc-text dc:truncate">{meta.shortTitle}</div>
                      <div className="dc:text-xs text-dc-text-muted dc:truncate">{meta.cubeName}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Help text when no fields are available */}
      {!shouldSkipQuery &&
        availableFields.measures.length === 0 &&
        availableFields.dimensions.length === 0 &&
        availableFields.timeDimensions.length === 0 && (
          <div className="dc:text-center text-dc-text-muted dc:text-sm dc:py-4">
            <p>{t('chart.config.noFields')}</p>
          </div>
        )}
    </div>
  )
}
